import { useState, FC, useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { Menu } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { RoomInput } from '../widgets/editor/RoomInput';

// Widgets
import { EditorSidebar } from '../widgets/editor/EditorSidebar';
import { RoomGrid } from '../widgets/editor/RoomGrid';
import { EstimateTable } from '../widgets/editor/EstimateTable';
import { EmptyState } from '../widgets/editor/EmptyState';
import { TemplatePicker } from '@/features/room';

import { useRooms } from '@/shared/hooks/useRooms';
import { useEstimate } from '@/features/estimate';
import type { CalculatedRoom } from '@/features/estimate';
import { useDatabase } from '@/shared/hooks/useDatabase';
import { useUIStore, useToastStore, useProjectStore } from '@/stores';
import { exportToExcel } from '../utils/exportToExcel';
import { RoomDims, Opening, Partition, WallElement, type Service, EstimateItem } from '@/shared/model';
import { roundTo } from '@/shared/lib/number';
import { cloneEstimateItemForRoom } from '@/entities/room';
import { hasAnySyncChange } from '@/entities/estimate';
import { Button } from '@/shared/ui/base';
import { ResponsiveModal } from '@/shared/ui/responsive-modal';
import { Logo } from '@/shared/ui/Logo';
import { CalculatedEstimateItem } from '@/shared/model';
import { MaterialsTab, useMaterials } from '@/features/materials';
import { MarginAnalyticsPanel, computeMargin, useUpgradeModal, TrialBanner } from '@/features/billing';
import { useAuthStore } from '@/stores/useAuthStore';
import { useServiceStore } from '@/stores/useServiceStore';
import { SessionWidget } from '@/features/auth';
import { track } from '@/shared/lib/analytics';
import { OnboardingChecklist } from '@/features/onboarding';

const EditorPage: FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const addToast = useToastStore((s) => s.addToast);
  const currency = useUIStore((s) => s.currency);
  const openModal = useUIStore((s) => s.openModal);

  // Template picker modal state (TMPL-01)
  const [templatePickerOpen, setTemplatePickerOpen] = useState(false);
  const addRoomFromTemplate = useProjectStore((s) => s.addRoomFromTemplate);
  
  // --- Data Hooks ---
  const { 
    rooms, meta, updateMeta, activeProjectId, activeRoomId, setActiveRoomId, 
    addRoom, updateRoom, safetyMargin, setSafetyMargin, syncPrices,
    commitVersion, loadProjectSnapshot,
    updateRoomDims, updateRoomOpenings, updateRoomPartitions, updateRoomWallElements,
    duplicateRoom, updateRoomName,
    // global layer (projectItems) surfaced from the updated hook
    projectItems, projectOverrides, setProjectItems, addProjectItem, updateProjectItem, removeProjectItem, addServicesToProject
  } = useRooms();

  const { services, categories } = useDatabase();

  // --- Calculated Data ---
  const estimate = useEstimate();
  const { activeRoom, projectTotal, totalArea, globalTotal, projectItems: calcProjectItems, rooms: calculatedRooms } = estimate;

  // stable map for tiles — enhanced for visual scale + minimal mobile info (floor, walls, cost, size)
  const roomCalcMap = Object.fromEntries(
    (calculatedRooms || []).map((r: CalculatedRoom) => {
      const g = r.geometry || {};
      const density = (r.calculations?.total || 0) / Math.max(g.floorArea || 1, 1); // cost per m² floor for color scale
      const dims = r.dims || {};
      const dimsLabel = `${dims.length || 0}×${dims.width || 0}×${dims.height || 0}`;
      return [r.id, { 
        floorArea: g.floorArea || 0,
        wallArea: g.wallArea || 0,
        cost: r.calculations?.total || 0, 
        itemCount: (r.items || r.calculations?.items || []).length,
        density: Math.round(density),
        dimsLabel,
      }];
    })
  );

  // --- Plan + Billing ---
  const entitled = useAuthStore((s) => s.entitled);
  const rawServices = useServiceStore((s) => s.rawServices);
  const openUpgrade = useUpgradeModal((s) => s.openUpgrade);

  const marginResult = useMemo(
    () => computeMargin(rooms, projectItems || [], safetyMargin, rawServices, projectOverrides),
    [rooms, projectItems, safetyMargin, rawServices, projectOverrides],
  );

  // --- Copy Service State ---
  const [copyingItem, setCopyingItem] = useState<CalculatedEstimateItem | null>(null);
  const [selectedRoomIds, setSelectedRoomIds] = useState<string[]>([]);

  const handleCopyConfirm = () => {
    if (!copyingItem || selectedRoomIds.length === 0) return;

    selectedRoomIds.forEach((targetRoomId) => {
      const targetRoom = rooms.find((r) => r.id === targetRoomId);
      if (!targetRoom) return;

      const newItem = cloneEstimateItemForRoom(copyingItem);

      updateRoom(targetRoomId, {
        items: [...(targetRoom.items || []), newItem],
      });
    });

    addToast(
      t('editor.messages.copied_to_rooms', 'Услуга "{{name}}" скопирована в {{count}} помещений.', {
        name: copyingItem.nameKey ? t(copyingItem.nameKey, copyingItem.name) : copyingItem.name,
        count: selectedRoomIds.length,
      }),
      'success'
    );
    setCopyingItem(null);
    setSelectedRoomIds([]);
  };

  // Auto-select removed per user clarification: when returning from room editor, no forced selection.
  // Main view shows tiles + global without auto-picking a room.

  // --- UI State ---
  const [isSidebarOpen, setIsSidebarOpen] = useState(() =>
    typeof window !== 'undefined' && window.innerWidth >= 1024
  );

  // MAT-04: Gather material norms from all services used in the active room.
  // Declared here (before handlers) so handleDownloadExcel can reference materialTotals.
  // When materialsTabEnabled is false, MaterialsTab self-hides, but we still compute
  // norms here so hooks are called unconditionally (Rules of Hooks).
  const materialsTabEnabled = useUIStore((s) => s.materialsTabEnabled);
  const activeRoomNorms = useMemo(() => {
    if (!activeRoom || !services) return [];
    const serviceList = (Array.isArray(services) ? services : Object.values(services).flat()) as Service[];
    const norms: Array<{ materialId: string; norm: number; surfaceType?: string }> = [];
    for (const item of activeRoom.items) {
      const svc = serviceList.find((s) => s.id === item.serviceId);
      if (svc?.materials) {
        for (const mn of svc.materials) {
          // Deduplicate by materialId — use first occurrence
          if (!norms.find((n) => n.materialId === mn.materialId)) {
            norms.push({ materialId: mn.materialId, norm: mn.norm, surfaceType: mn.surfaceType });
          }
        }
      }
    }
    return norms;
  // materialsTabEnabled is not included: norms don't change when the tab is toggled
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeRoom, services]);

  // CR-01 (MAT-04): compute material totals for the active room.
  // Called unconditionally (Rules of Hooks). Only forwarded to exports when tab is on.
  // activeRoom is cast via the same pattern used in MaterialsTab prop.
  const { materialTotals } = useMaterials(
    activeRoom as unknown as import('@/shared/model').Room | null,
    activeRoomNorms,
  );

  // --- Handlers ---
  const handleDownloadExcel = useCallback(() => {
     if (services) {
        try {
          // CR-01 (MAT-04): pass materialTotals only when the tab is enabled and has lines.
          // Tab off → undefined → materials section absent (unchanged labour-only export).
          const matTotals = (materialsTabEnabled && materialTotals.lines.length > 0)
            ? materialTotals
            : undefined;
          exportToExcel(calculatedRooms, services, projectTotal, currency, meta, calcProjectItems, safetyMargin, matTotals, projectOverrides);
          track('export', { format: 'excel' });
          // ONBOARD-01: step 3 detection — set hasExported so the checklist knows this session exported
          useUIStore.getState().setHasExported(true);
        } catch (err) {
          console.error('Excel export failed', err);
        }
     }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [services, calculatedRooms, projectTotal, currency, meta, calcProjectItems, safetyMargin, materialsTabEnabled, materialTotals, projectOverrides]);

  const handleSyncPrices = useCallback(() => {
     if (services) {
        const stats = syncPrices(services);
        if (hasAnySyncChange(stats)) {
          // Compose the details string from only the axes that actually changed, so the
          // report never claims a change that didn't happen (CATALOG-03 — the report must
          // name what changed, not just whether the price-only counter moved).
          const details = [
            stats.count ? t('editor.messages.sync_changed_prices', 'prices: {{count}}', { count: stats.count }) : null,
            stats.unitChanged > 0 ? t('editor.messages.sync_changed_units', 'units: {{count}}', { count: stats.unitChanged }) : null,
            stats.categoryChanged > 0 ? t('editor.messages.sync_changed_categories', 'categories: {{count}}', { count: stats.categoryChanged }) : null,
            stats.nameChanged > 0 ? t('editor.messages.sync_changed_names', 'names: {{count}}', { count: stats.nameChanged }) : null,
          ].filter(Boolean).join(' · ');
          const diffText = stats.diff !== 0 ? `, ${stats.diff > 0 ? '+' : ''}${Math.round(stats.diff)}` : '';
          addToast(
            t('editor.messages.sync_report', 'Estimate updated — {{details}}{{diff}}', { details, diff: diffText }),
            'success'
          );
        } else {
          addToast(t('editor.messages.prices_actual', 'Цены уже актуальны'), 'info');
        }
     }
  }, [services, syncPrices, addToast, t]);

  const handleResetPriceToCatalog = useCallback((itemId: string) => {
    if (!activeRoom || !services) return;
    const item = activeRoom.items.find(i => i.id === itemId);
    if (!item) return;
    const serviceList = (Array.isArray(services) ? services : Object.values(services).flat()) as Service[];
    const service = serviceList.find((s) => s.id === item.serviceId);
    if (!service) return;
    updateRoom(activeRoom.id, {
      items: activeRoom.items.map(i => i.id === itemId ? { ...i, priceLabor: service.priceLabor, priceIsManual: false } : i)
    });
    addToast(t('editor.messages.price_reset', 'Цена сброшена к каталогу'), 'info');
  }, [activeRoom, services, updateRoom, addToast, t]);

  const handleUpdateRoom = useCallback((newDims: Partial<RoomDims>) => {
    if (activeRoom) updateRoomDims(activeRoom.id, newDims);
  }, [activeRoom, updateRoomDims]);

  const handleUpdateOpenings = useCallback((ops: Opening[]) => {
    if (activeRoom) updateRoomOpenings(activeRoom.id, ops);
  }, [activeRoom, updateRoomOpenings]);

  const handleUpdatePartitions = useCallback((parts: Partition[]) => {
    if (activeRoom) updateRoomPartitions(activeRoom.id, parts);
  }, [activeRoom, updateRoomPartitions]);

  const handleUpdateWallElements = useCallback((els: WallElement[]) => {
    if (activeRoom) updateRoomWallElements(activeRoom.id, els);
  }, [activeRoom, updateRoomWallElements]);

  const handleUpdateItem = useCallback((id: string, f: keyof EstimateItem, v: EstimateItem[keyof EstimateItem]) => {
    if (!activeRoom) return;
    updateRoom(activeRoom.id, { 
      items: activeRoom.items.map(i => i.id === id ? { ...i, [f]: v, ...(f === 'priceLabor' ? { priceIsManual: true } : {}) } : i) 
    });
  }, [activeRoom, updateRoom]);

  const handleRemoveItem = useCallback((id: string) => {
    if (!activeRoom) return;
    updateRoom(activeRoom.id, { items: activeRoom.items.filter(i => i.id !== id) });
  }, [activeRoom, updateRoom]);

  // Global layer handlers (separate from room logic)
  const handleUpdateGlobalItem = useCallback((id: string, f: keyof EstimateItem, v: EstimateItem[keyof EstimateItem]) => {
    updateProjectItem(id, f, v);
  }, [updateProjectItem]);

  const handleRemoveGlobalItem = useCallback((id: string) => {
    removeProjectItem(id);
  }, [removeProjectItem]);

  const handleResetGlobalPrice = useCallback((id: string) => {
    if (!services) return;
    const item = (projectItems || []).find(i => i.id === id);
    if (!item) return;
    const serviceList = (Array.isArray(services) ? services : Object.values(services).flat()) as Service[];
    const svc = serviceList.find((s: Service) => s.id === item.serviceId);
    if (!svc) return;
    updateProjectItem(id, 'priceLabor', svc.priceLabor);
  }, [projectItems, services, updateProjectItem]);

  return (
    <div className="flex h-screen w-full bg-slate-100 dark:bg-slate-950 overflow-hidden font-sans text-slate-900 dark:text-slate-100 transition-colors duration-300">
      
      <EditorSidebar
          isOpen={isSidebarOpen}
          onClose={() => setIsSidebarOpen(false)}
          projectTotal={projectTotal}
          totalArea={totalArea}
          safetyMargin={safetyMargin}
          setSafetyMargin={setSafetyMargin}
          onDownloadExcel={handleDownloadExcel}
          onSyncPrices={handleSyncPrices}
          meta={meta}
          updateMeta={updateMeta}
          activeProjectId={activeProjectId}
          commitVersion={commitVersion}
          onLoadSnapshot={loadProjectSnapshot}
          rooms={calculatedRooms}
          projectItems={calcProjectItems}
          materialTotals={materialsTabEnabled && materialTotals.lines.length > 0 ? materialTotals : undefined}
          projectOverrides={projectOverrides}
      />

      <main className={`flex-1 flex flex-col relative h-full min-w-0 transition-all duration-300 ${isSidebarOpen ? 'lg:pl-[320px]' : 'lg:pl-0'}`}>
        <header className="flex flex-col bg-white/80 dark:bg-slate-900/80 backdrop-blur-md border-b border-slate-200 dark:border-slate-800 z-30 shrink-0">
          {/* First row: Logo + Menu + Quick actions (56px on mobile) */}
          <div className="h-14 flex items-center px-4 lg:h-16 lg:px-6 lg:flex-row lg:items-center">
            <div className="flex items-center gap-3">
              <AnimatePresence>
                {!isSidebarOpen && (
                  <motion.button
                    initial={{ opacity: 0, scale: 0.8 }}
                    animate={{ opacity: 1, scale: 1 }}
                    exit={{ opacity: 0, scale: 0.8 }}
                    onClick={() => setIsSidebarOpen(true)}
                    className="p-2 hover:bg-slate-100 dark:hover:bg-slate-800 rounded-xl transition-colors lg:hidden"
                    aria-label={t('common.open_sidebar', 'Открыть панель')}
                  >
                    <Menu size={22} />
                  </motion.button>
                )}
              </AnimatePresence>

              <button
                type="button"
                className="flex flex-col items-start cursor-pointer hover:opacity-80 transition-opacity rounded-lg focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-500 focus-visible:ring-offset-2 dark:focus-visible:ring-offset-slate-900"
                onClick={() => navigate('/')}
                title={t('common.go_to_projects', 'К проектам / выбор проекта')}
                aria-label={t('common.go_to_projects', 'К проектам / выбор проекта')}
              >
                <Logo className="h-7 w-auto" />
              </button>
            </div>
            <div className="ml-auto flex items-center gap-2">
              <TrialBanner />
              <SessionWidget />
            </div>
          </div>

          {/* ONBOARD-01: Inline onboarding checklist — below the top bar row, non-blocking */}
          <div className="px-4 lg:px-6">
            <OnboardingChecklist />
          </div>

          {/* No more horizontal tabs — replaced by visual square tiles grid (per vision: tiles = rooms with identical internal logic; modal materializes the full "room tab" content) */}
        </header>

        <div className="flex-1 overflow-y-auto scroll-smooth custom-scrollbar [scrollbar-gutter:stable]">
          <div className="max-w-6xl mx-auto p-4 md:p-8 space-y-8 pb-32">
            { !activeRoom ? (
              <>
                {/* === ГЛАВНАЯ СТРАНИЦА С ПЛИТАМИ === */}
                {rooms.length === 0 && (
                  <EmptyState
                    onAddRoom={() => addRoom(activeRoomId)}
                    onSelectTemplate={(templateId) => {
                      const newId = addRoomFromTemplate(templateId);
                      setActiveRoomId(newId);
                    }}
                  />
                )}
                {rooms.length > 0 && (
                  <>
                    <RoomGrid
                      rooms={rooms}
                      calculated={roomCalcMap}
                      activeRoomId={activeRoomId}
                      onSelectRoom={(id) => {
                        setActiveRoomId(id);
                      }}
                      onAddRoom={() => setTemplatePickerOpen(true)}
                      onDuplicateRoom={duplicateRoom}
                      onDeleteRoomRequest={(id) => openModal('room_delete_confirm', { id })}
                      onUpdateRoomName={updateRoomName}
                      onReorderRooms={(newIds) => {
                        const byId = Object.fromEntries(rooms.map(r => [r.id, r]));
                        const reordered = newIds.map(id => byId[id]).filter(Boolean);
                        useProjectStore.getState().setRooms(reordered);
                      }}
                    />

                    {/* Глобальный блок — только здесь, под плитками */}
                    <div className="pt-4">
                      <div className="mb-3 px-1 flex items-baseline justify-between">
                        <div className="font-bold text-sm text-slate-700 dark:text-slate-200">
                          {t('editor.global_services_title', 'Глобальный слой / Общие позиции проекта')}
                        </div>
                        <div className="text-xs text-emerald-600 dark:text-emerald-400 font-mono tabular-nums">
                          {t('editor.global_total', 'Итого глобал: {{val}}', { val: roundTo(globalTotal || 0, 0) })}
                        </div>
                      </div>

                      <EstimateTable
                        items={calcProjectItems || []}
                        categories={categories}
                        onUpdateItem={handleUpdateGlobalItem}
                        onRemoveItem={handleRemoveGlobalItem}
                        onCopyToRooms={() => { /* later */ }}
                        onOpenServices={() => openModal('service_selector', { target: 'global' })}
                        onOpenPackages={() => openModal('recipe_selector', { target: 'global' })}
                        onShowManualInfo={() => addToast(t('editor.messages.manual_mode'), 'info')}
                        onShowAutoInfo={(item) => addToast(t('editor.messages.auto_mode', { base: roundTo(item.baseQty || 0, 2), adj: item.adjustment }), 'info')}
                        onResetPriceToCatalog={handleResetGlobalPrice}
                      />
                    </div>

                    {/* Margin analytics — entitled-gated (MARGIN-01, D-05: trial users get Pro UX) */}
                    <MarginAnalyticsPanel
                      entitled={entitled}
                      billedTotal={marginResult.billedTotal}
                      baseCostTotal={marginResult.baseCostTotal}
                      onUpgradeClick={() => openUpgrade('analytics')}
                    />
                  </>
                )}
              </>
            ) : (
              /* === РЕДАКТОР КОМНАТЫ (без плиток в этом блоке) === */
              <div className="space-y-6">
                <div className="flex items-center justify-between px-1">
                  <div className="font-bold text-lg text-slate-800 dark:text-slate-100">
                    {activeRoom.displayName || activeRoom.name || 'Помещение'}
                  </div>
                  <button
                    onClick={() => setActiveRoomId(null)}
                    className="text-sm px-3 py-1 rounded-lg border border-slate-300 dark:border-slate-700 hover:bg-slate-50 dark:hover:bg-slate-800"
                  >
                    {t('editor.back_to_tiles', '← Назад к плитам')}
                  </button>
                </div>

                {/* Блок геометрии */}
                <section
                  className="bg-white dark:bg-slate-900 rounded-2xl p-4 md:p-6 border border-slate-200 dark:border-slate-800"
                >
                  <RoomInput 
                    length={activeRoom.dims.length}
                    width={activeRoom.dims.width}
                    height={activeRoom.dims.height}
                    openings={activeRoom.openings}
                    partitions={activeRoom.partitions}
                    wallElements={activeRoom.wallElements}
                    geometry={activeRoom.geometry}
                    onChange={handleUpdateRoom}
                    onOpeningsChange={handleUpdateOpenings}
                    onPartitionsChange={handleUpdatePartitions}
                    onWallElementsChange={handleUpdateWallElements}
                  />
                </section>

                {/* Блок услуг комнаты */}
                <div>
                  <div className="mb-3 px-1 flex items-baseline justify-between">
                    <div className="font-bold text-sm text-slate-700 dark:text-slate-200">
                      {t('editor.room_services_title', 'Услуги и пакеты этого помещения')}
                    </div>
                  </div>
                  <EstimateTable
                    items={activeRoom?.calculations?.items || []}
                    categories={categories}
                    onUpdateItem={handleUpdateItem}
                    onRemoveItem={handleRemoveItem}
                    onCopyToRooms={(item) => { setCopyingItem(item); setSelectedRoomIds([]); }}
                    onOpenServices={() => openModal('service_selector')}
                    onOpenPackages={() => openModal('recipe_selector')}
                    onShowManualInfo={() => addToast(t('editor.messages.manual_mode'), 'info')}
                    onShowAutoInfo={(item) => addToast(t('editor.messages.auto_mode', { base: roundTo(item.baseQty || 0, 2), adj: item.adjustment }), 'info')}
                    onResetPriceToCatalog={handleResetPriceToCatalog}
                  />
                </div>

                {/* MAT-04: Materials tab — self-hides when materialsTabEnabled=false */}
                {materialsTabEnabled && (
                  <div className="bg-white dark:bg-slate-900 rounded-2xl p-4 md:p-6 border border-slate-200 dark:border-slate-800">
                    <div className="mb-3 font-bold text-sm text-slate-700 dark:text-slate-200">
                      {t('materials.tab', 'Materials')}
                    </div>
                    <MaterialsTab
                      room={activeRoom as unknown as import('@/shared/model').Room}
                      norms={activeRoomNorms}
                    />
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      </main>

      {/* Template picker modal — TMPL-01: opened by RoomGrid/EmptyState onAddRoom, CommandMenu (Pitfall 4: max-w-md, NO p-0) */}
      <ResponsiveModal
        open={templatePickerOpen}
        onOpenChange={setTemplatePickerOpen}
        title={t('templates.picker_title', 'Zacznij od szablonu')}
        className="max-w-md"
      >
        <TemplatePicker
          onSelect={(templateId) => {
            const newId = addRoomFromTemplate(templateId);
            setActiveRoomId(newId);
            setTemplatePickerOpen(false);
          }}
          onSkip={() => {
            addRoom(activeRoomId);
            setTemplatePickerOpen(false);
          }}
        />
      </ResponsiveModal>

      {copyingItem && (
        <ResponsiveModal
          open={true}
          onOpenChange={(open) => !open && setCopyingItem(null)}
          title={t('editor.copy_modal.title', 'Копировать в другие помещения')}
          className="max-w-md p-6"
        >
          <div className="space-y-4">
            <p className="text-sm text-slate-500 dark:text-slate-400">
              {t('editor.copy_modal.subtitle', 'Выберите помещения, в которые нужно скопировать работу:')}
            </p>
            <div className="font-bold text-slate-800 dark:text-slate-100 border p-3 rounded-xl bg-slate-50 dark:bg-slate-900 border-slate-200 dark:border-slate-800">
              {copyingItem.nameKey ? t(copyingItem.nameKey, copyingItem.name) : copyingItem.name}
            </div>
            
            <div className="max-h-[30vh] overflow-y-auto space-y-2 border rounded-xl p-3 border-slate-200 dark:border-slate-800">
              {rooms
                .filter((r) => r.id !== activeRoom?.id)
                .map((room) => {
                  const isChecked = selectedRoomIds.includes(room.id);
                  return (
                    <label 
                      key={room.id}
                      className="flex items-center gap-3 p-2 hover:bg-slate-50 dark:hover:bg-slate-800/50 rounded-lg cursor-pointer transition-colors"
                    >
                      <input 
                        type="checkbox"
                        checked={isChecked}
                        onChange={() => {
                          setSelectedRoomIds(prev => 
                            isChecked ? prev.filter(id => id !== room.id) : [...prev, room.id]
                          );
                        }}
                        className="w-4 h-4 text-blue-600 rounded border-slate-300 dark:border-slate-700"
                      />
                      <span className="text-sm font-medium text-slate-700 dark:text-slate-200">{room.displayName || room.name}</span>
                    </label>
                  );
                })}
              {rooms.length <= 1 && (
                <div className="text-center text-xs text-slate-500 dark:text-slate-400 py-4">
                  {t('editor.copy_modal.no_other_rooms', 'Нет других комнат для копирования')}
                </div>
              )}
            </div>

            <div className="flex gap-2 justify-between">
              <Button
                variant="secondary"
                size="sm"
                onClick={() => {
                  const otherRooms = rooms.filter(r => r.id !== activeRoom?.id);
                  const allSelected = selectedRoomIds.length === otherRooms.length;
                  setSelectedRoomIds(allSelected ? [] : otherRooms.map(r => r.id));
                }}
                disabled={rooms.length <= 1}
              >
                {selectedRoomIds.length === rooms.filter(r => r.id !== activeRoom?.id).length 
                  ? t('common.deselect_all', 'Снять все') 
                  : t('common.select_all', 'Выбрать все')}
              </Button>
            </div>

            <div className="flex gap-3 justify-end pt-2 border-t border-slate-100 dark:border-slate-800">
              <Button
                variant="secondary"
                onClick={() => setCopyingItem(null)}
              >
                {t('common.cancel', 'Отмена')}
              </Button>
              <Button
                onClick={handleCopyConfirm}
                disabled={selectedRoomIds.length === 0}
              >
                {t('common.copy', 'Копировать')}
              </Button>
            </div>
          </div>
        </ResponsiveModal>
      )}
    </div>
  );
}

export default EditorPage;
